import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { ImageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import React, { useEffect, useState } from "react";
import { useEmblaCarousel } from "embla-carousel/react";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import ThumbnailVideoIcon from "@NA/Icons/ThumbnailVideoIcon";
import { ButtonProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { connect } from "react-redux";
import getColor from "@insite/mobius/utilities/getColor";
import { getUpdatedImages } from "@NA/Utils/Utility";

interface OwnProps {
    product?: ProductModel;
    image?: ImageModel;
    onClick?: () => void;
    isTabView?: boolean;
    selectedIndex?: number;
    onSelectImage?: (index: number) => void;
    setFullImageModalIsOpen?: (item: boolean) => void;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});
const getImageCss = (borderColor: string) => css`
    width: 100%;
    height: 100%;
    cursor: pointer;
    justify-content: flex-start;
    img {
        width: 200px;
        height: 200px;
    }
`;

export interface ADIProductImageCarouselStyles {
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    image?: LazyImageProps;
    selectedImage?: LazyImageProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonProps;
    thumbnailIcon?: InjectableCss;
    dotsContainer?: InjectableCss;
    dots?: InjectableCss;
    dotsSelected?: InjectableCss;
}

export const adiProductImageCarouselStyles: ADIProductImageCarouselStyles = {
    mainContainer: { gap: 0 },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    carouselGridItem: { width: 12 },
    carouselWrapper: {
        css: css`
            width: 100%;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
            gap: 10px;
        `,
    },
    carouselSlide: {
        css: css`
            flex: 0 0 ${100}%;
            padding: 0;
            position: relative;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            text-align: center;
            position: relative;
            padding: 0px 3px 0;
            > button {
                position: relative;
                width: 100%;
            }
        `,
    },
    image: {
        css: getImageCss("common.border"),
    },
    selectedImage: {
        css: getImageCss("text.main"),
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    thumbnailIcon: {
        css: css`
            position: absolute;
            z-index: 9;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            svg {
                width: 46px;
                height: 46px;
            }
        `,
    },
    dotsContainer: {
        css: css`
            display: flex;
            justify-content: center;
            margin-top: 10px;
        `,
    },
    dots: {
        css: css`
            width: 10px;
            height: 10px;
            border-radius: 50%;
            border: 1px solid ${getColor("primary")};
            background: #fff;
            margin: 0 5px;
        `,
    },
    dotsSelected: {
        css: css`
            width: 10px;
            height: 10px;
            border-radius: 50%;
            border: 1px solid ${getColor("primary")};
            margin: 0 5px;
            background-color: ${getColor("primary")};
        `,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const styles = adiProductImageCarouselStyles;

const ADIProductCarousal = ({
    product,
    image,
    onClick,
    onSelectImage,
    selectedIndex,
    productSettings,
    setFullImageModalIsOpen,
}: Props) => {
    const [selectedImageIndex, setSelectedImageIndex] = useState(selectedIndex);
    const [displayVideo, setDisplayVideo] = useState(false);
    const [multiVarientImages, setMultiVarientImages] = useState<ImageModel[]>([]);  
    const mvpvideoLink1 = product?.properties?.video1Url;
    const mvpvideoLink2 = product?.properties?.video2Url;
    const thumbnailBg1 = product?.properties?.thumbnail1Url;
    const thumbnailBg2 = product?.properties?.thumbnail2Url;
    const imagesData = product?.images || [];

    useEffect(() => {
        setSelectedImageIndex(selectedIndex);
    }, [selectedIndex]);

    const imageClickHandler = (index: number, imageType: string = "Static") => {
        setSelectedImageIndex(index);
        onSelectImage?.(index);
        if (!isMobile && imageType !== "swipe") {
            setFullImageModalIsOpen?.(true);
        }
        if (imageType === "video") {
            setDisplayVideo(true);
        } else {
            setDisplayVideo(false);
        }
    };

    const [emblaRef, embla] = useEmblaCarousel();
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [isMobile, setIsMobile] = React.useState(false);

    const setCanScroll = () => {
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);
        embla.on("select", () => {
            imageClickHandler(embla.selectedScrollSnap(), "swipe");
        });
    }, [embla]);

    useEffect(() => {
        if (!embla || !product?.images) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll: 1,
            draggable: true,
            loop: false,
        });

        setCanScroll();
    }, [embla, product, isMobile, displayVideo, selectedImageIndex]);

    React.useEffect(() => {
        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, []);
    const onWindowResize = () => {
        if (window.innerWidth >= 700) {
            setIsMobile(false);
        } else {
            setIsMobile(true);
        }
    };
    const isMultiVariantProduct = !!(
        product?.variantTypeId?.length && product?.variantTypeId !== null
        );
    const getMultiVariantImages = React.useCallback(() => {
        if (!isMultiVariantProduct) return;        
            const updatedImages = getUpdatedImages(product);            
    
            setMultiVarientImages((prevImages) => {
                const areImagesEqual =
                JSON.stringify(prevImages) === JSON.stringify(updatedImages);
                return areImagesEqual ? prevImages : updatedImages;
            });
        }, [isMultiVariantProduct, imagesData, mvpvideoLink1, mvpvideoLink2, thumbnailBg1, thumbnailBg2, product?.id]);
    
        useEffect(() => {
            getMultiVariantImages();            
        }, [getMultiVariantImages]);
    
        useEffect(() => {
            
        }, [multiVarientImages]);


    const getProductImageThumbPath = (productImage: ImageModel) => {
        if (productImage.imageType === "video") {
            return productImage.smallImagePath;
        }
        if (productImage.imageType === "Static") {
            if(productImage.imageAltText === "AlternateImage") {
                return productImage.largeImagePath 
            }
            return productImage.largeImagePath || productImage.mediumImagePath;
        }
        if (productImage.imageType === "360" && productSettings.imageProvider === "SIRV") {
            return productImage.mediumImagePath + (productImage.mediumImagePath.indexOf("?") > 0 ? "&thumb" : "?thumb");
        }
        return "";
    };

    if (!product?.images) return null;

    const mobileMultiVariantImage = () => {
        return (
            <>
                <StyledWrapper>
                    <GridItem {...styles.carouselGridItem}>
                        <StyledWrapper {...styles.carouselWrapper}>
                            {displayVideo && isMobile && selectedImageIndex !== undefined ? (
                                <iframe
                                    className="adi_vidyard_video_iframe"
                                    allowFullScreen={true}
                                    style={{
                                        minHeight: "285px",
                                        height: "100%",
                                        width: "100%",
                                        border: "none",
                                    }}
                                    src={`https://play.vidyard.com/${multiVarientImages[selectedImageIndex].largeImagePath}`}
                                ></iframe>
                            ) : null}
                            <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                                <StyledWrapper {...styles.carouselSlidesContainer}>
                                    {multiVarientImages.map((productImage, index) => (
                                        <StyledWrapper {...styles.carouselSlide} key={productImage.id}>
                                            <StyledWrapper {...styles.carouselSlideInner}>
                                                <Clickable onClick={() => imageClickHandler(index, productImage.imageType)}>
                                                    {productImage.imageType === "video" ? (
                                                        <StyledWrapper {...styles.thumbnailIcon}>
                                                            <ThumbnailVideoIcon />
                                                        </StyledWrapper>
                                                    ) : null}
                                                    <LazyImage
                                                        {...(index === selectedImageIndex
                                                            ? styles.selectedImage
                                                            : styles.image)}
                                                        src={getProductImageThumbPath(productImage)}
                                                        altText={productImage.imageAltText}
                                                    />
                                                </Clickable>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    ))}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </GridItem>
                    <StyledWrapper {...styles.dotsContainer}>
                        {multiVarientImages.length > 1 && multiVarientImages.map((item: ImageModel, index: number) => (
                            <StyledWrapper
                                {...(index === selectedImageIndex ? styles.dotsSelected : styles.dots)}
                            ></StyledWrapper>
                        ))}
                    </StyledWrapper>
                </StyledWrapper>
            </>
        )
    }

    return (
        <>
         {isMultiVariantProduct ? (mobileMultiVariantImage()) : (
            <StyledWrapper>
                <GridItem {...styles.carouselGridItem}>
                    <StyledWrapper {...styles.carouselWrapper}>
                        {displayVideo && isMobile && selectedImageIndex !== undefined ? (
                            <iframe
                                className="adi_vidyard_video_iframe"
                                allowFullScreen={true}
                                style={{
                                    minHeight: "285px",
                                    height: "100%",
                                    width: "100%",
                                    border: "none",
                                }}
                                src={`https://play.vidyard.com/${product.images[selectedImageIndex].largeImagePath}`}
                            ></iframe>
                        ) : null}
                        <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                            <StyledWrapper {...styles.carouselSlidesContainer}>
                                {product?.images.map((productImage, index) => (
                                    <StyledWrapper {...styles.carouselSlide} key={productImage.id}>
                                        <StyledWrapper {...styles.carouselSlideInner}>
                                            <Clickable onClick={() => imageClickHandler(index, productImage.imageType)}>
                                                {productImage.imageType === "video" ? (
                                                    <StyledWrapper {...styles.thumbnailIcon}>
                                                        <ThumbnailVideoIcon />
                                                    </StyledWrapper>
                                                ) : null}
                                                <LazyImage
                                                    {...(index === selectedImageIndex
                                                        ? styles.selectedImage
                                                        : styles.image)}
                                                    src={getProductImageThumbPath(productImage)}
                                                    altText={productImage.imageAltText}
                                                />
                                            </Clickable>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ))}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>
                <StyledWrapper {...styles.dotsContainer}>
                    {product.images.length > 1 && product.images.map((item: ImageModel, index: number) => (
                        <StyledWrapper
                            {...(index === selectedImageIndex ? styles.dotsSelected : styles.dots)}
                        ></StyledWrapper>
                    ))}
                </StyledWrapper>
            </StyledWrapper>
         )}
        </>
        
    );
};

export default connect(mapStateToProps)(ADIProductCarousal);
